import 'bootstrap-daterangepicker/daterangepicker.css';
import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { FaEye } from 'react-icons/fa6';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import ReactReadMoreReadLess from "react-read-more-read-less";
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { KTSVG } from '../../../_metronic/helpers';
import { PageTitle } from '../../../_metronic/layout/core';
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp';
import DateComp from '../../../components/dateComponent/DateComp';
import DateRangeComp from '../../../components/dateComponent/DateRangeComp';
import DateTimeComp from '../../../components/dateComponent/DateTimeComp';
import { LoadingComponent } from '../../../components/loadingComponent/LoadingComponent';
import ListingPageSkeleton from '../../../components/loadingComponent/ListingPageSkeleton';
import NoRecordFound from '../../../components/noRecord/NoRecordFound';
import Pagination from '../../../components/pagination/Pagination';
import RefreshComponent from '../../../components/refreshComponent/RefreshComponent';
import SearchBarComponent from '../../../components/searchBar/SearchBarComponent';
import TableHeading from '../../../components/tableHeading/TableHeading';
import TableSettingMenu from '../../../components/tableHeading/TableSettingMenu';
import SearchSelect from '../../../components/textFields/SearchSelect';
import TextField from '../../../components/textFields/TextField';
import { constraintConfig } from '../../../constraintConfig';
import { selectAuth } from '../../../features/authSlice';
import { useAppSelector } from '../../../hooks';
import { useGetCardExpenseItemQuery, useLazyGetExpenseItemsByExpenseItemIdQuery } from '../../../services/ExpenseApi';
import { useExpenseCategoryLookupQuery, useGetAllExpenseTypeQuery, useGetAllRecordSourceLookupQuery, useGetPaymentMethodLookupQuery } from '../../../services/GeneralApi';
import useColumnVisibility from '../../customHooks/useColumnVisibility';
import labelKey from "../../localization/label.json";
import tosterKey from "../../localization/toster.json";
import AssignToExistingExpenseModal from './AssignToExistingExpenseModal';
import AssignToNewExpenseModal from './AssignToNewExpenseModal';
import CategoriesWithIcons from './CategoriesWithIcons';
import DeleteExpenseModal from './DeleteExpenseModal';
import { AddExpenseItemModal } from './update-single-Item/AddExpenseItemModal';
import SingleItemDetailModal from './update-single-Item/SingleItemDetailModal';
import { UpdateExpenseItemModal } from './update-single-Item/UpdateExpenseItemModal';
import UploadDirectReceipt from './UploadDirectReceipt';
import { Alert, Spinner } from 'react-bootstrap';
import MergeExpenseModal from './MergeExpenseGate';
import SsRowActions, { SsRowActionItem } from '../../../components/tableActions/SsRowActions';
import UnMergeConfirmationModal from './UnMergeConfirmationModal';

interface LightboxData {
    imageURL: string;
}

// Column configuration with proper typing
interface ColumnConfig {
    id: string;
    labelKey?: keyof typeof labelKey;
    sortable: boolean;
    dataKey?: string;
    customLabel: string;
    className?: string;
    labelClassName?: string;
}

const COLUMN_CONFIG: ColumnConfig[] = [
    { id: 'checkbox', customLabel: 'Select', sortable: false },
    { id: 'itemDate', labelKey: 'lineItemDate', customLabel: 'Line Item Date', sortable: true, dataKey: 'itemDate' },
    { id: 'expenseType', labelKey: 'expenseType', customLabel: 'Expense Type', sortable: true, dataKey: 'expenseType' },
    { id: 'expenseCategory', labelKey: 'category', customLabel: 'Category', sortable: true, dataKey: 'expenseCategory' },
    { id: 'amountInOriginalCurrency', labelKey: 'amountInOrigionalCurrency', customLabel: 'Amount In Original Currency', sortable: true, dataKey: 'amountInOriginalCurrency', className: 'text-end', labelClassName: 'text-end' },
    { id: 'unitPrice', labelKey: 'unitPrice', customLabel: 'Unit Price', sortable: true, dataKey: 'unitPrice', className: 'text-end', labelClassName: 'text-end' },
    { id: 'amount', labelKey: 'amount', customLabel: 'Amount', sortable: true, dataKey: 'amount', className: 'text-end', labelClassName: 'text-end' },
    { id: 'reimbursableDistance', labelKey: 'reimbursableDistance', customLabel: 'Reimbursable Distance', sortable: true, dataKey: 'reimbursableDistance', className: 'text-end', labelClassName: 'justify-content-end text-end' },
    { id: 'vander', labelKey: 'vendor', customLabel: 'Vendor', sortable: true, dataKey: 'vendor' },
    { id: 'paymentMethod', labelKey: 'paymentMethod', customLabel: 'Payment Method', sortable: true, dataKey: 'paymentMethod' },
    { id: 'attachment', labelKey: 'attachment', customLabel: 'Attachment', sortable: false, dataKey: 'attachment' },
    { id: 'description', labelKey: 'description', customLabel: 'Description', sortable: false, dataKey: 'description' },
    { id: 'source', labelKey: 'source', customLabel: 'Source', sortable: false, dataKey: 'source' },
    { id: 'createdDateTime', labelKey: 'createdDate', customLabel: 'Created Date', sortable: true, dataKey: 'createdDateTime' },
    { id: 'updatedDate', labelKey: 'updatedDate', customLabel: 'Updated Date', sortable: true, dataKey: 'updatedDate' },
    { id: 'createdBy', customLabel: 'Created By', sortable: true, dataKey: 'createdBy' },
    { id: 'actions', labelKey: 'actions', customLabel: 'Actions', sortable: false, dataKey: 'actions' },
];

const getLabelText = (col: ColumnConfig): string => {
    if (col.labelKey && labelKey[col.labelKey]) {
        return labelKey[col.labelKey];
    }
    return col.customLabel;
};

const DraftItemsPage = () => {
    const { hiddenColumns, handleToggleColumn } = useColumnVisibility({ pageName: 'expenseLineItems' });
    const visibleColumns = COLUMN_CONFIG.filter(col => hiddenColumns.includes(col.id) && col.id !== 'actions' && col.id !== 'checkbox');
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxData, setLightboxData] = useState<LightboxData>({ imageURL: '' });
    const { userAccountID } = useAppSelector(selectAuth);
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10);
    const [searchStr, setSearchStr] = useState(''); // debounce search
    const [immediateSearchStr, setImmediateSearchStr] = useState('');
    const [sortBy, setSortBy] = useState("itemDate");
    const [sortOrder, setSortOrder] = useState("desc");
    const [filterByCategory, setFilterByCategory] = useState("");
    const [filterByStartDate, setFilterByStartDate] = useState("");
    const [filterByEndDate, setFilterByEndDate] = useState("");
    const [filterBySubCategory, setFilterBySubCategory] = useState("");
    const [selectedExpenseItemIds, setSelectedExpenseItemIds] = useState<number[]>([]);
    const [sortedColumn, setSortedColumn] = useState("itemDate");
    const [isTableHeadChecked, setIsTableHeadChecked] = useState(false);

    const [filterByCreatedStartDate, setFilterByCreatedStartDate] = useState("")
    const [filterByCreatedEndDate, setFilterByCreatedEndDate] = useState("")
    const [filterByAmount, setFilterByAmount] = useState(0)
    const [filterByRecordSourceId, setFilterByRecordSourceId] = useState(0)
    const [filterByPaymentMethodId, setFilterByPaymentMethodId] = useState(0)
    const [filterByExpenseTypeId, setFilterByExpenseTypeId] = useState(0)

    const [localFilterByCreatedStartDate, setLocalFilterByCreatedStartDate] = useState("")
    const [localFilterByCreatedEndDate, setLocalFilterByCreatedEndDate] = useState("")
    const [localFilterByAmount, setLocalFilterByAmount] = useState(0)
    const [localFilterByRecordSourceId, setLocalFilterByRecordSourceId] = useState(0)
    const [localFilterByPaymentMethodId, setLocalFilterByPaymentMethodId] = useState(0)
    const [localFilterByExpenseTypeId, setLocalFilterByExpenseTypeId] = useState(0)

    const [showUpdateExpenseModal, setShowUpdateExpenseModal] = useState(false)


    const [localFilterByStartDate, setLocalFilterByStartDate] = useState("");
    const [localFilterByEndDate, setLocalFilterByEndDate] = useState("");
    const [localFilterByCategory, setlocalFilterByCategory] = useState("");
    const [isApplyingFilters, setIsApplyingFilters] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    const PAGE_INFO_SESSION_KEY = 'ss-expense-line-items-info-dismissed'
    const [showPageInfo, setShowPageInfo] = useState(() => {
        try {
            return sessionStorage.getItem(PAGE_INFO_SESSION_KEY) !== '1'
        } catch {
            return true
        }
    })
    const dismissPageInfo = useCallback(() => {
        setShowPageInfo(false)
        try {
            sessionStorage.setItem(PAGE_INFO_SESSION_KEY, '1')
        } catch {
            /* ignore */
        }
    }, [])

    const getRecordSourceMeta = useCallback((recordSourceId?: number) => {
        if (recordSourceId === constraintConfig.recordSourceId.WebApp) {
            return { src: '/media/icons/figmaIcons/Web.svg', label: 'Website' }
        }
        if (recordSourceId === constraintConfig.recordSourceId.Mobile) {
            return { src: '/media/icons/figmaIcons/Mobile.svg', label: 'Mobile' }
        }
        if (recordSourceId === constraintConfig.recordSourceId.BankFeed) {
            return { src: '/media/icons/figmaIcons/Bank.svg', label: 'Bank' }
        }
        if (recordSourceId === constraintConfig.recordSourceId.GorillaExpense) {
            return { src: '/media/icons/figmaIcons/Gorilla.svg', label: 'Gorilla Expense' }
        }
        return { src: '', label: '' }
    }, [])

    const getAttachmentUploadedLabel = useCallback((itemData: any) => {
        const uploaded = itemData?.receiptUploadedDateTime || itemData?.receiptUploadedDate
        if (!uploaded) return ''
        return moment.utc(uploaded).local().format('MM-DD-YYYY hh:mm A')
    }, [])

    const { data: expenseCategory } = useExpenseCategoryLookupQuery('')
    const { data: paymentMethodLookup } = useGetPaymentMethodLookupQuery('')
    const { data: recordSourceLookup } = useGetAllRecordSourceLookupQuery('')
    const { data: getAllExpenseType } = useGetAllExpenseTypeQuery('')

    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [selectedItem, setSelectedItem] = useState(null)
    const handleUnmergeClick = (item: any) => {
        setSelectedItem(item)
        setShowConfirmModal(true)
    }

    const [expenseItemById, { data: expenseItemByIdData, isFetching: expenseItemByIdDataFetching }] = useLazyGetExpenseItemsByExpenseItemIdQuery({})
    const { data, isFetching: isLoading, refetch, isSuccess } = useGetCardExpenseItemQuery({
        userAccountId: userAccountID,
        pageNumber: page,
        pageSize: pageSize,
        sortBy: sortBy,
        sortOrder: sortOrder,
        searchStr: searchStr,
        filterByCategory: filterByCategory,
        startDate: filterByStartDate,
        endDate: filterByEndDate,
        createdStartDate: filterByCreatedStartDate,
        createdEndDate: filterByCreatedEndDate,
        filterByAmount: filterByAmount,
        filterByRecordSourceId: filterByRecordSourceId,
        filterByPaymentMethodId: filterByPaymentMethodId,
        filterByExpenseTypeId: filterByExpenseTypeId,
    })

    const showMobileSkeleton = isLoading && !data?.result
    const handlePageClick = (e: any) => {
        setPage(e.selected + 1);
    };
    const handlePageSizeChange = (e: any) => {
        const newSize = parseInt(e.target.value);
        setPageSize(newSize);
        setPage(1); // Reset page number to 1
    };

    const handleSort = (property: string) => {
        setSortBy(property);
        if (sortedColumn === property) {
            setSortOrder(prevSortOrder => (prevSortOrder === "asc" ? "desc" : "asc"));
        } else {
            setSortedColumn(property);
            setSortOrder("asc");
        }
        // Perform additional sorting logic...
    };


    //per page record
    const handlePageRecords = (e: any) => {
        setPage(1); // Reset the page to 1
    };
    const handleSearchChange = (e: any) => {
        const { value } = e.target;
        setImmediateSearchStr(value); // Update immediate search state
        delayedSearch(value); // Update debounced search state
        setPage(1)
    };

    const delayedSearch = useCallback(
        debounce((searchValue) => {
            setSearchStr(searchValue);
        }, 500),
        []
    );
    const handleCategoryChange = (e: any) => {
        setlocalFilterByCategory(e.value);
    };
    const handlePaymentMethodChange = (e: any) => {
        setLocalFilterByPaymentMethodId(e.value);
    };
    const handleRecordSourceChange = (e: any) => {
        setLocalFilterByRecordSourceId(e.value);
    };
    const handleExpenseTypeChange = (e: any) => {
        setLocalFilterByExpenseTypeId(e.value);
    };
    const handleFilterByAmount = (e: any) => {
        setLocalFilterByAmount(e.target.value);
    };
    const handleDateRangeChange = (event: any, picker: any) => {
        const startDateFormatted = moment(picker.startDate).format('MM-DD-YYYY');
        const endDateFormatted = moment(picker.endDate).format('MM-DD-YYYY');

        setLocalFilterByStartDate(startDateFormatted);
        setLocalFilterByEndDate(endDateFormatted);
    };
    const handleCancelDateSelection = (event: any, picker: any) => {
        // Reset the state to empty strings
        setFilterByStartDate("");
        setFilterByEndDate("");
        setLocalFilterByStartDate("");
        setLocalFilterByEndDate("");
    };
    const handleCreatedDateRangeChange = (event: any, picker: any) => {
        const startDateFormatted = moment(picker.startDate).format('MM-DD-YYYY');
        const endDateFormatted = moment(picker.endDate).format('MM-DD-YYYY');

        setLocalFilterByCreatedStartDate(startDateFormatted);
        setLocalFilterByCreatedEndDate(endDateFormatted);
    };
    const handleCreatedCancelDateSelection = (event: any, picker: any) => {
        // Reset the state to empty strings
        setFilterByCreatedStartDate("");
        setFilterByCreatedEndDate("");
        setLocalFilterByCreatedStartDate("");
        setLocalFilterByCreatedEndDate("");
    };

    const applyFilters = () => {
        setIsApplyingFilters(true);
        setFilterByStartDate(localFilterByStartDate);
        setFilterByEndDate(localFilterByEndDate);
        setFilterByCategory(localFilterByCategory);

        setFilterByCreatedStartDate(localFilterByCreatedStartDate)
        setFilterByCreatedEndDate(localFilterByCreatedEndDate)
        setFilterByAmount(localFilterByAmount)
        setFilterByRecordSourceId(localFilterByRecordSourceId)
        setFilterByPaymentMethodId(localFilterByPaymentMethodId)
        setFilterByExpenseTypeId(localFilterByExpenseTypeId)
        setPage(1)
    };
    const resetFilters = () => {
        setFilterByStartDate('');
        setFilterByEndDate('');
        setFilterByCategory("");
        setLocalFilterByStartDate('');
        setLocalFilterByEndDate('');
        setlocalFilterByCategory('');
        setFilterByCreatedStartDate('');
        setFilterByCreatedEndDate('');
        setFilterByAmount(0);
        setFilterByRecordSourceId(0);
        setFilterByPaymentMethodId(0);
        setFilterByExpenseTypeId(0);
        setLocalFilterByCreatedStartDate('');
        setLocalFilterByCreatedEndDate('');
        setLocalFilterByAmount(0);
        setLocalFilterByRecordSourceId(0);
        setLocalFilterByPaymentMethodId(0);
        setLocalFilterByExpenseTypeId(0);
    }
    const debouncedRefetch = debounce(() => {
        refetch().finally(() => {
            setIsApplyingFilters(false);
        });
    }, 300);
    useEffect(() => {
        if (isApplyingFilters) {
            debouncedRefetch();
        }
    }, [isApplyingFilters]);


    useEffect(() => {
        if (searchStr === "" ||
            sortOrder === "asc" ||
            sortBy === ""
        ) {
            refetch();
        }
    }, [sortOrder, sortBy, searchStr]);
    const [showAddExpenseItemModal, setShowAddExpenseItemModal] = useState(false)

    const [showExpenseItemDetailModal, setShowExpenseItemDetailModal] = useState(false)


    const [showUpdateExpenseItemModal, setShowUpdateExpenseItemModal] = useState(false)
    const [showExpenseItemData, setShowExpenseItemData] = useState({})
    const [viewOnlyData, setViewOnlyData] = useState(false)

    const expenseItemModalHandler = () => {
        setShowUpdateExpenseItemModal((prev) => !prev);
    };

    const handleTableHeadChange = (e: any) => {
        const isChecked = e.target.checked;
        // console.log(`Table head checked: ${isChecked}`); 
        setIsTableHeadChecked(isChecked);
        const allExpenseItems = data?.result?.data || [];
        // console.log('All expense items:', allExpenseItems); 

        const filteredExpenseItems = allExpenseItems.filter((item: any) => item?.paymentMethod?.id !== 3);
        // console.log('Filtered expense items:', filteredExpenseItems);
        if (isChecked) {
            const allExpenseItemIds = filteredExpenseItems.map((item: any) => item.expenseItemId);
            // console.log('Expense item IDs to be checked:', allExpenseItemIds);

            setSelectedExpenseItemIds((prevIds) => {
                const updatedIds = Array.from(new Set([...prevIds, ...allExpenseItemIds]));
                // console.log('Updated selected expense item IDs:', updatedIds);
                return updatedIds;
            });
        } else {
            setSelectedExpenseItemIds([]);
        }

        // Update isTableHeadChecked state
        //  setIsTableHeadChecked(isChecked);

        setIsTableHeadChecked(isChecked && filteredExpenseItems.length === allExpenseItems.length);

    };



    // handleCheckbox
    const handleCheckItem = (e: any, itemId: any) => {
        const isChecked = e.target.checked;

        setSelectedExpenseItemIds((prevItems) => {
            // Create a copy of the previous selectedExpenseItemIds array
            const updatedIds = [...prevItems];

            if (isChecked) {
                // If the checkbox is checked, add the itemId to the selectedExpenseItemIds array
                updatedIds.push(itemId);
            } else {
                // If the checkbox is unchecked, remove the itemId from the selectedExpenseItemIds array
                const index = updatedIds.indexOf(itemId);
                if (index !== -1) {
                    updatedIds.splice(index, 1);
                }
            }

            // Update the table head checkbox state based on the current state of the checkboxes in the body
            // setIsTableHeadChecked(updatedIds.length === data?.result?.data?.length);
            setIsTableHeadChecked(updatedIds.length === pageSize * (page - 1) + data?.result?.data?.length);

            return updatedIds;
        });
    };

    useEffect(() => {
        setIsTableHeadChecked(false); // Reset isTableHeadChecked when page changes
    }, [page]);

    const resetCheckboxState = () => {
        setIsTableHeadChecked(false);
        setSelectedExpenseItemIds([]);
    };


    const noItemsSelected = selectedExpenseItemIds.length === 0 && !isTableHeadChecked;
    const addExistingTooltip = noItemsSelected
        ? 'Select expense items from the listing to attach them to an existing expense report.'
        : 'Attach selected expense items to an existing expense report.';
    const addNewExpenseTooltip = noItemsSelected
        ? 'Select expense items from the listing to attach them to a new expense report.'
        : 'Attach selected expense items to a new expense report.';
    const addNewItemTooltip = 'Create new expense line items directly. You can attach them to reports later.';

    const [showAddNewExpenseModal, setShowAddNewExpenseModal] = useState(false)
    const addNewExpenseModalHandler = () => {
        setShowAddNewExpenseModal((prev) => !prev);
    };

    const [showAddExistingExpensetModal, setShowAddExistingExpenseModal] = useState(false)
    const eidtProjectModalHandler = () => {
        setShowAddExistingExpenseModal((prev) => !prev);
    };

    const [showExpenseDeleteModal, setShowExpenseDeleteModal] = useState(false)
    const [expenseDeleteData, setExpenseDeleteData] = useState({});

    const deleteHandler = () => {
        setShowExpenseDeleteModal((prev) => !prev);
    };

    const isPdfFile = (filePath: string | undefined) => {
        if (!filePath) return false;
        const fileExtension = filePath.toLowerCase().split('.').pop();
        return fileExtension === 'pdf';
    };
    const openPdfInBrowserOrLightbox = (attachmentFile: string | undefined) => {
        if (attachmentFile) {
            if (isPdfFile(attachmentFile)) {
                // For PDF files, open in the browser
                window.open(attachmentFile, '_blank');
            } else {
                // For image files, open in the Lightbox
                setLightboxData({ imageURL: attachmentFile });
                setLightboxOpen(true);
            }
        }
    };

    const handleRefresh = () => {
        resetCheckboxState();
        refetch()
            .then(() => {
                if (isSuccess) {
                    toast.success(tosterKey.dataRefreshedSuccesfully);
                }
            });
    };
    return (
        <>
            <Tooltip
                id="attachment-view"
                place="top"
                positionStrategy="fixed"
                render={({ activeAnchor }) => {
                    const uploaded = activeAnchor?.getAttribute('data-uploaded') || ''
                    const sourceLabel = activeAnchor?.getAttribute('data-source-label') || ''
                    const sourceIcon = activeAnchor?.getAttribute('data-source-icon') || ''
                    return (
                        <div className='ss-attachment-tooltip text-start'>
                            <div>Click here for View attachment in new Tab</div>
                            {uploaded ? (
                                <div className='mt-1'>{labelKey.receiptUploadedDateTime}: {uploaded}</div>
                            ) : null}
                            {sourceLabel ? (
                                <div className='d-flex align-items-center gap-2 mt-1'>
                                    {sourceIcon ? (
                                        <span className='ss-source-icon-chip'>
                                            <img src={sourceIcon} alt="" width={16} height={16} />
                                        </span>
                                    ) : null}
                                    <span>{labelKey.source}: {sourceLabel}</span>
                                </div>
                            ) : null}
                        </div>
                    )
                }}
            />
            <AddExpenseItemModal
                show={showAddExpenseItemModal}
                handleClose={() => setShowAddExpenseItemModal(false)}
                viewOnlyData={false}
                refetch={refetch} />
            <UpdateExpenseItemModal
                show={showUpdateExpenseItemModal}
                handleClose={() => setShowUpdateExpenseItemModal(false)}
                // data={showExpenseItemData}
                data={expenseItemByIdData?.result}
                viewOnlyData={viewOnlyData}
                refetch={refetch} />
            <SingleItemDetailModal
                show={showExpenseItemDetailModal}
                handleClose={() => setShowExpenseItemDetailModal(false)}
                data={expenseItemByIdData?.result}
                viewOnlyData={viewOnlyData}
                isFetching={expenseItemByIdDataFetching} />
            <AssignToNewExpenseModal
                show={showAddNewExpenseModal}
                handleClose={() => {
                    setShowAddNewExpenseModal(false);
                    resetCheckboxState();
                }}
                selectedExpenseItemIds={selectedExpenseItemIds}
                refetchDraftItems={refetch}
            />
            <AssignToExistingExpenseModal
                show={showAddExistingExpensetModal}
                handleClose={() => {
                    setShowAddExistingExpenseModal(false);
                    resetCheckboxState();
                }}
                selectedExpenseItemIds={selectedExpenseItemIds}
                refetch={refetch}
            />
            <DeleteExpenseModal
                show={showExpenseDeleteModal}
                handleClose={() => setShowExpenseDeleteModal(false)}
                data={expenseDeleteData}
                refetch={refetch} />

            <MergeExpenseModal
                selectedDraftData={showExpenseItemData}
                show={showUpdateExpenseModal}
                handleClose={() => {
                    setShowUpdateExpenseModal(false);
                    resetCheckboxState();
                }}
                refetch={refetch}
            />
            <UnMergeConfirmationModal
                show={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                selectedDraftData={selectedItem}
                onSuccess={refetch}
            />
            <div className='card'>

            <PageTitle breadcrumbs={[]}>{labelKey.expenseLineItems}</PageTitle>

                {/* begin::Header */}
                <div className='card-header border-0 pt-5 pb-2 flex-wrap gap-2 align-items-center ss-line-items-toolbar'>
                {showPageInfo && (
                    <Alert
                        variant="primary"
                        className='d-flex align-items-center justify-content-between ss-page-info-alert ss-page-info-alert--full w-100 mt-0 mb-2 py-2 px-4'
                    >
                        <div className='ss-page-info-alert__body flex-grow-1 me-3'>
                            <p className='mb-0 fs-7'>
                                Corporate Credit Card line items cannot be manually added to the report as it is auto-generated from the Corporate Credit Card Report.
                            </p>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-icon btn-active-color-primary ss-page-info-alert__close flex-shrink-0"
                            aria-label="Close"
                            title="Close"
                            onClick={dismissPageInfo}
                        >
                            <i className="fas fa-times fs-7" />
                        </button>
                    </Alert>
                )}
                    <div className='d-flex align-items-center flex-wrap gap-2 min-w-0 flex-grow-1'>
                        <SearchBarComponent
                            placeholder='Expense Item'
                            value={immediateSearchStr}
                            onChange={handleSearchChange}
                            className='ss-user-toolbar-search-input'
                            mainClassName='ss-table-search--sm my-0'
                        />
                        <Tooltip id="refreshData" place="top" />
                        <RefreshComponent onClick={handleRefresh} />
                        {!showFilters ? (
                            <button
                                type="button"
                                className="btn btn-sm ss-pill-btn ss-pill-btn-filter-active"
                                onClick={() => setShowFilters(true)}
                            >
                                <i className="fa fa-filter me-1 text-white" />
                                Show Filters
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-sm ss-pill-btn ss-pill-btn-filter-active"
                                onClick={() => setShowFilters(false)}
                            >
                                <i className="fa fa-eye-slash me-1 text-white" />
                                Hide Filters
                            </button>
                        )}
                    </div>
                    <div className='d-flex align-items-center flex-wrap gap-2 ms-lg-auto'>
                        <Tooltip
                            id="draft-item-toolbar-actions"
                            place="top"
                            positionStrategy="fixed"
                            className="ss-line-items-toolbar-tooltip"
                        />
                        <span
                            className="d-inline-block"
                            data-tooltip-id="draft-item-toolbar-actions"
                            data-tooltip-content={addExistingTooltip}
                        >
                            <button
                                type="button"
                                className="btn btn-sm btn-primary ss-line-items-toolbar-btn ss-smart-btn"
                                onClick={() => {
                                    eidtProjectModalHandler();
                                }}
                                disabled={noItemsSelected}
                                style={noItemsSelected ? { pointerEvents: 'none' } : undefined}
                                aria-label={addExistingTooltip}
                            >
                                <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                                {labelKey.addExistingExpense}
                            </button>
                        </span>
                        <span
                            className="d-inline-block"
                            data-tooltip-id="draft-item-toolbar-actions"
                            data-tooltip-content={addNewExpenseTooltip}
                        >
                            <button
                                type="button"
                                className="btn btn-sm btn-primary ss-line-items-toolbar-btn ss-smart-btn"
                                onClick={() => {
                                    addNewExpenseModalHandler();
                                    setViewOnlyData(false);
                                }}
                                disabled={noItemsSelected}
                                style={noItemsSelected ? { pointerEvents: 'none' } : undefined}
                                aria-label={addNewExpenseTooltip}
                            >
                                <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                                {labelKey.addNewExpense}
                            </button>
                        </span>
                        <span
                            className="d-inline-block"
                            data-tooltip-id="draft-item-toolbar-actions"
                            data-tooltip-content={addNewItemTooltip}
                        >
                            <button
                                type="button"
                                className="btn btn-sm btn-primary ss-line-items-toolbar-btn ss-smart-btn"
                                onClick={() => setShowAddExpenseItemModal(true)}
                                aria-label={addNewItemTooltip}
                            >
                                <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                                {labelKey.addNewItem}
                            </button>
                        </span>
                    </div>
                </div>
                <div className='card-body py-2'>
                    {showFilters && (
                    <div className='ss-line-items-filters ss-listing-filters ss-params-row d-flex flex-wrap align-items-end pb-2 mb-2'>
                        <div className='ss-line-items-filters__field'>
                            <SearchSelect
                                label={`${labelKey.expenseType}:`}
                                options={[
                                    ...(Array.isArray(getAllExpenseType?.result) ? getAllExpenseType.result.map((option: any) => ({
                                        value: option.id,
                                        label: option?.value || option?.title,
                                    })) : [])
                                ]}
                                placeholder='Select Expense Type'
                                selectClass='filter-valueContainer mb-0'
                                value={localFilterByExpenseTypeId && localFilterByExpenseTypeId !== 0
                                    ? {
                                        value: localFilterByExpenseTypeId,
                                        label: getAllExpenseType?.result.find((option: any) => option.id === localFilterByExpenseTypeId)?.value
                                            || getAllExpenseType?.result.find((option: any) => option.id === localFilterByExpenseTypeId)?.title
                                    }
                                    : null
                                }
                                onChange={handleExpenseTypeChange}
                            />
                        </div>
                        <div className='ss-line-items-filters__field'>
                            <SearchSelect
                                label={`${labelKey.category}:`}
                                options={[
                                    ...(Array.isArray(expenseCategory?.result) ? expenseCategory.result.map((option: any) => ({
                                        value: option.id,
                                        label: option?.value,
                                    })) : [])
                                ]}
                                placeholder='Select Category'
                                selectClass='filter-valueContainer mb-0'
                                value={localFilterByCategory && localFilterByCategory !== ''
                                    ? {
                                        value: localFilterByCategory,
                                        label: expenseCategory?.result.find((option: any) => option.id === localFilterByCategory)?.value
                                    }
                                    : null
                                }
                                onChange={handleCategoryChange}
                            />
                        </div>
                        <div className='ss-line-items-filters__field'>
                            <SearchSelect
                                label={`${labelKey.paymentMethod}:`}
                                options={[
                                    ...(Array.isArray(paymentMethodLookup?.result) ? paymentMethodLookup.result.map((option: any) => ({
                                        value: option.id,
                                        label: option?.value,
                                    })) : [])
                                ]}
                                placeholder='Select Payment Method'
                                selectClass='filter-valueContainer mb-0'
                                value={localFilterByPaymentMethodId && localFilterByPaymentMethodId !== 0
                                    ? {
                                        value: localFilterByPaymentMethodId,
                                        label: paymentMethodLookup?.result.find((option: any) => option.id === localFilterByPaymentMethodId)?.value
                                    }
                                    : null
                                }
                                onChange={handlePaymentMethodChange}
                            />
                        </div>
                        <div className='ss-line-items-filters__field'>
                            <SearchSelect
                                label={`${labelKey.source}:`}
                                options={[
                                    ...(Array.isArray(recordSourceLookup?.result) ? recordSourceLookup.result.map((option: any) => ({
                                        value: option.id,
                                        label: option?.value,
                                    })) : [])
                                ]}
                                placeholder='Select Source'
                                selectClass='filter-valueContainer mb-0'
                                value={localFilterByRecordSourceId && localFilterByRecordSourceId !== 0
                                    ? {
                                        value: localFilterByRecordSourceId,
                                        label: recordSourceLookup?.result.find((option: any) => option.id === localFilterByRecordSourceId)?.value
                                    }
                                    : null
                                }
                                onChange={handleRecordSourceChange}
                            />
                        </div>
                        <div className='ss-line-items-filters__field'>
                            <TextField
                                type='number'
                                label='Item Amount'
                                placeholder='Search By Amount'
                                value={localFilterByAmount ? String(localFilterByAmount) : ''}
                                onChange={handleFilterByAmount}
                                fieldClass='mb-0'
                            />
                        </div>
                        <div className='ss-line-items-filters__field ss-line-items-filters__field--date'>
                            <DateRangeComp
                                label="Item Date Range"
                                startDate={localFilterByStartDate}
                                endDate={localFilterByEndDate}
                                onApply={handleDateRangeChange}
                                onCancel={handleCancelDateSelection}
                                value={localFilterByStartDate && localFilterByEndDate ? `${localFilterByStartDate} - ${localFilterByEndDate}` : ""}
                                className="mb-0 ss-filter-control"
                            />
                        </div>
                        <div className='ss-line-items-filters__field ss-line-items-filters__field--date'>
                            <DateRangeComp
                                label="Created Date Range"
                                startDate={localFilterByCreatedStartDate}
                                endDate={localFilterByCreatedEndDate}
                                onApply={handleCreatedDateRangeChange}
                                onCancel={handleCreatedCancelDateSelection}
                                value={localFilterByCreatedStartDate && localFilterByCreatedEndDate ? `${localFilterByCreatedStartDate} - ${localFilterByCreatedEndDate}` : ""}
                                className="mb-0 ss-filter-control"
                            />
                        </div>
                        <div className='ss-line-items-filters__actions d-flex align-items-end gap-2'>
                            <button type='button' className='btn btn-sm btn-danger text-nowrap ss-smart-btn' onClick={resetFilters}>
                                Reset Filters
                            </button>
                            <button type='button' className='btn btn-sm btn-primary text-nowrap ss-smart-btn' onClick={applyFilters}>
                                {isApplyingFilters ? (
                                    <>
                                        <span>loading...</span>
                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                    </>
                                ) : (
                                    'Apply Filters'
                                )}
                            </button>
                        </div>
                    </div>
                    )}
                    <Tooltip id="Un-Merge-item" place="left" positionStrategy="fixed" />
                    <Tooltip id="Merge-item" place="left" positionStrategy="fixed" />
                    <Tooltip id="Delete-Item" place="left" positionStrategy="fixed" />
                    <Tooltip id="Update-Item" place="left" positionStrategy="fixed" />
                    <Tooltip id="Detail-Item" place="left" positionStrategy="fixed" />

                    <div className={`table-responsive table-height${showMobileSkeleton ? ' d-none d-lg-block' : ''}`}>
                        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                            <thead>
                                <tr className='fw-bold text-muted'>
                                    {hiddenColumns.includes('checkbox') && (
                                        <TableHeading
                                            columnId='checkbox'
                                            className="ps-4"
                                        >
                                            {data?.result?.data?.length > 0 ? (
                                                <div className='form-check form-check-sm form-check-custom form-check-solid me-3'>
                                                    <input
                                                        type="checkbox"
                                                        className='form-check-input cursor-pointer'
                                                        checked={isTableHeadChecked}
                                                        onChange={handleTableHeadChange}
                                                    />
                                                </div>
                                            ) : null}
                                        </TableHeading>
                                    )}
                                    {visibleColumns.map((col) => (
                                        <TableHeading
                                            key={col.id}
                                            label={getLabelText(col)}
                                            columnId={col.id}
                                            sortedColumn={col.sortable ? sortedColumn : undefined}
                                            sortOrder={col.sortable ? sortOrder : undefined}
                                            handleSort={col.sortable ? handleSort : undefined}
                                            className={col.className}
                                            labelClassName={col.labelClassName}
                                        />
                                    ))}
                                    {hiddenColumns.includes('actions') && (
                                        <th className='text-end rounded-end pe-2 border-0 sticky-table-actions'>
                                            <span className='me-2'>{labelKey.actions}</span>
                                            <TableSettingMenu>
                                                {COLUMN_CONFIG.map((col) => {
                                                    if (col.id === 'checkbox' && !(data?.result?.data?.length > 0)) {
                                                        return null;
                                                    }
                                                    return (
                                                        <TextField
                                                            key={col.id}
                                                            rightLabel={getLabelText(col)}
                                                            type="checkbox"
                                                            onChange={() => handleToggleColumn(col.id)}
                                                            checked={hiddenColumns.includes(col.id)}
                                                            fieldClass='mb-4'
                                                        />
                                                    );
                                                })}
                                            </TableSettingMenu>
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {!isLoading &&
                                    <>
                                        {data?.result?.data?.length > 0 ? (
                                            <>
                                                {data?.result?.data?.map((data: any, index: any) => (
                                                    <tr
                                                        key={data?.expenseItemId}
                                                        className={`${selectedExpenseItemIds.includes(data.expenseItemId) ? 'highlighted-row' : ''}`}>
                                                        {hiddenColumns.includes('checkbox') && (
                                                            <td className='ps-4'>
                                                                {data?.recordSourceId !== constraintConfig.recordSourceId.BankFeed &&
                                                                    <TextField
                                                                        type="checkbox"
                                                                        fieldClass='form-check-input cursor-pointer'
                                                                        checked={isTableHeadChecked || selectedExpenseItemIds.includes(data.expenseItemId)}
                                                                        onChange={(e) => handleCheckItem(e, data.expenseItemId)}
                                                                        disabled={data?.recordSourceId === constraintConfig.recordSourceId.BankFeed}
                                                                    // disabled={data?.ocrPassed === false}
                                                                    />
                                                                }
                                                            </td>
                                                        )}
                                                        {/* {hiddenColumns.includes('sr') && (
                                                            <td>
                                                                {(page - 1) * pageSize + index + 1}
                                                            </td>
                                                        )} */}
                                                        {hiddenColumns.includes('itemDate') && (
                                                            <td><DateComp formattedDate={data.itemDate} /></td>
                                                        )}
                                                        {hiddenColumns.includes('expenseType') && (
                                                            <td className='ps-5'>{data?.expenseType?.title}</td>
                                                        )}
                                                        {hiddenColumns.includes('expenseCategory') && (
                                                            <td className='ps-5'><CategoriesWithIcons itemData={data} /></td>
                                                        )}
                                                        {hiddenColumns.includes('amountInOriginalCurrency') && (
                                                            <td className='ps-5'>{data?.amountInOrigionalCurrency}</td>
                                                        )}
                                                        {hiddenColumns.includes('unitPrice') && (
                                                            <td className='ps-5'>{data?.unitPrice}</td>
                                                        )}

                                                        {/* {hiddenColumns.includes('currency') && (
                                                            <td className='ps-10'>{data?.currency?.title}</td>
                                                        )} */}
                                                        {hiddenColumns.includes('amount') && (
                                                            <td className={`text-end ${data?.debitOrCreditIndicator === constraintConfig.debitOrCreditIndicator.creditIndicator ? 'text-danger' : null}`}>
                                                                <CurrencyComp amount={data?.amount} />
                                                            </td>
                                                        )}
                                                        {hiddenColumns.includes('reimbursableDistance') && (
                                                            <td className='text-end'>
                                                                {data?.expenseCategory?.id === constraintConfig.categoriesId.mileage ?
                                                                    <>
                                                                        {data?.reimbursableDistance}
                                                                    </>
                                                                    : null
                                                                }
                                                            </td>
                                                        )}
                                                        {hiddenColumns.includes('vander') && (
                                                            <td>{data?.vendor?.name !== " " ? data?.vendor?.name : "N/A"}</td>
                                                        )}
                                                        {hiddenColumns.includes('paymentMethod') && (
                                                            <td>{data?.paymentMethod?.title}</td>
                                                        )}

                                                        {/* {hiddenColumns.includes('location') && (
                                                            <td>{data?.expItemLocation?.title ? data?.expItemLocation?.title : "N/A"}</td>
                                                        )} */}
                                                        {hiddenColumns.includes('attachment') && (
                                                            <td>
                                                                <Tooltip id="upload-reciept" place="top" />
                                                                {/* {data?.expenseCategory?.id === constraintConfig.categoriesId.mileage ? '' :
                                                                    data?.attachmentFile === null || data?.attachmentFile === "null" || data?.attachmentFile === "undefined" || data?.attachmentFile === "" || data?.attachmentFile === "Receipt is not available" ? (
                                                                        data?.debitOrCreditIndicator === constraintConfig.debitOrCreditIndicator.creditIndicator ? null : (

                                                                            <div className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm ms-7'
                                                                                data-tooltip-id="upload-reciept" data-tooltip-content='Upload Reciept'
                                                                                onClick={() => setShowExpenseItemData(data)}
                                                                            >
                                                                                <UploadDirectReceipt data={showExpenseItemData} refetch={refetch} />
                                                                            </div>)
                                                                    ) :

                                                                        <FaEye className='ms-10' onClick={() => openPdfInBrowserOrLightbox(data?.attachmentFile)} />
                                                                } */}
                                                                {data?.attachmentFile === null ||
                                                                    data?.attachmentFile === "null" ||
                                                                    data?.attachmentFile === "undefined" ||
                                                                    data?.attachmentFile === "" ||
                                                                    data?.attachmentFile === "Receipt is not available" ? (
                                                                    data?.debitOrCreditIndicator === constraintConfig.debitOrCreditIndicator.creditIndicator ? null : (
                                                                        <div
                                                                            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm ms-7'
                                                                            data-tooltip-id="upload-reciept"
                                                                            data-tooltip-content='Upload Receipt'
                                                                            onClick={() => setShowExpenseItemData(data)}
                                                                        >
                                                                            <UploadDirectReceipt data={showExpenseItemData} refetch={refetch} />
                                                                        </div>
                                                                    )
                                                                )
                                                                    : (
                                                                        <FaEye
                                                                            className='ms-10 cursor-pointer'
                                                                            data-tooltip-id="attachment-view"
                                                                            data-uploaded={getAttachmentUploadedLabel(data)}
                                                                            data-source-label={getRecordSourceMeta(data?.recordSourceId).label}
                                                                            data-source-icon={getRecordSourceMeta(data?.recordSourceId).src}
                                                                            title="Click here for View attachment in new Tab"
                                                                            aria-label="Click here for View attachment in new Tab"
                                                                            onClick={() => openPdfInBrowserOrLightbox(data?.attachmentFile)}
                                                                        />
                                                                    )}

                                                                {lightboxOpen && (
                                                                    <Lightbox
                                                                        mainSrc={lightboxData.imageURL}
                                                                        onCloseRequest={() => setLightboxOpen(false)}
                                                                        imageCaption="Attachment"
                                                                        enableZoom={true}
                                                                        imagePadding={50}
                                                                    />
                                                                )}
                                                            </td>
                                                        )}
                                                        {/* <td>
                                                            {data?.ocrPassed === true ?
                                                                <span className="badge badge-light-success fs-7 fw-semibold">Passed</span> :
                                                                <span className="badge badge-light-danger fs-7 fw-semibold">Failed</span>
                                                            }
                                                        </td> */}
                                                        {hiddenColumns.includes('description') && (
                                                            <td className='text-wrap'>
                                                                {/* {data?.itemDetail?.length > 60 ? `${data?.itemDetail.substring(0, 40)}...` : data?.itemDetail} */}
                                                                {data && data.itemDetail && <ReactReadMoreReadLess
                                                                    charLimit={30}
                                                                    readMoreText={"Read more"}
                                                                    readLessText={"Read less"}
                                                                    readMoreClassName="readMore"
                                                                    readLessClassName="readLess"
                                                                >
                                                                    {data.itemDetail}
                                                                </ReactReadMoreReadLess>}
                                                            </td>
                                                        )}
                                                        {hiddenColumns.includes('source') && (
                                                            <td>
                                                                <Tooltip id="web-source" place="top" />
                                                                <Tooltip id="mobile-source" place="top" />
                                                                <Tooltip id="bank-source" place="top" />
                                                                <Tooltip id="gorilla-source" place="top" />

                                                                {data?.recordSourceId === constraintConfig.recordSourceId.WebApp ?
                                                                    <KTSVG path="/media/icons/figmaIcons/Web.svg" className='svg-icon-1 ms-4'
                                                                        data-tooltip-id="web-source" data-tooltip-content='Website' />
                                                                    :
                                                                    data?.recordSourceId === constraintConfig.recordSourceId.Mobile ?
                                                                        <KTSVG path="/media/icons/figmaIcons/Mobile.svg" className='svg-icon-1 ms-4'
                                                                            data-tooltip-id="mobile-source" data-tooltip-content='Mobile' /> :
                                                                        data?.recordSourceId === constraintConfig.recordSourceId.BankFeed ?
                                                                            <KTSVG path="/media/icons/figmaIcons/Bank.svg" className='svg-icon-1 ms-4'
                                                                                data-tooltip-id="bank-source" data-tooltip-content='Bank' />
                                                                            : data?.recordSourceId === constraintConfig.recordSourceId.GorillaExpense ?
                                                                                <KTSVG path="/media/icons/figmaIcons/Gorilla.svg" className='svg-icon-1 ms-4'
                                                                                    data-tooltip-id="gorilla-source" data-tooltip-content='Gorilla Expense' /> : null}
                                                            </td>
                                                        )}
                                                        {hiddenColumns.includes('createdDateTime') && (
                                                            <td><DateTimeComp formattedDate={data.createdDate} /></td>
                                                        )}
                                                        {hiddenColumns.includes('updatedDate') && (
                                                            <td><DateTimeComp formattedDate={data.updatedDate} /></td>
                                                        )}

                                                        {hiddenColumns.includes('createdBy') && (
                                                            <td>

                                                                {data?.onBehalfOfUser?.firstName &&
                                                                    <div className='d-flex align-items-center'>
                                                                        <div className='symbol symbol-40px symbol-circle me-3'>
                                                                            {/* {data?.manager?.imageUrl ? <img src={data?.manager?.imageUrl} alt="" /> : */}
                                                                            <div className="symbol-label fs-3 bg-light-info text-info text-capitalize">{data?.onBehalfOfUser?.firstName?.charAt(0)}</div>
                                                                            {/* } */}
                                                                        </div>
                                                                        <div className='d-flex justify-content-start flex-column'>
                                                                            <p className='text-capitalize mb-0'>
                                                                                {data?.onBehalfOfUser?.firstName}  {data?.onBehalfOfUser?.lastName}
                                                                            </p>
                                                                            <span className='text-muted fw-semibold text-muted text-lowercase d-block fs-7'>
                                                                                {data?.onBehalfOfUser?.email}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                }

                                                            </td>
                                                        )}
                                                        {hiddenColumns.includes('actions') && (
                                                            <td className='text-end sticky-table-actions' onClick={(e) => e.stopPropagation()}>
                                                                <SsRowActions
                                                                    menuId={`draft-item-actions-${data?.expenseItemId}`}
                                                                    actions={[
                                                                        ...(data?.mergeExpenseitemId > 0 &&
                                                                        data?.recordSourceId === constraintConfig.recordSourceId.BankFeed &&
                                                                        data?.paymentMethod?.id === constraintConfig.paymentMethod.corporateCreditCard
                                                                            ? [{
                                                                                key: 'unmerge',
                                                                                label: 'Un-Merge Expense',
                                                                                tooltipId: 'Un-Merge-item',
                                                                                icon: <KTSVG path="/media/icons/duotune/general/unmerge.svg" className="svg-icon-3" />,
                                                                                onClick: () => handleUnmergeClick(data),
                                                                            }]
                                                                            : []),
                                                                        ...(data?.recordSourceId !== constraintConfig.recordSourceId.BankFeed &&
                                                                        data?.paymentMethod?.id !== constraintConfig.paymentMethod.corporateCreditCard
                                                                            ? [{
                                                                                key: 'merge',
                                                                                label: 'Merge Expense',
                                                                                tooltipId: 'Merge-item',
                                                                                icon: <KTSVG path="/media/icons/duotune/general/merge.svg" className="svg-icon-3" />,
                                                                                onClick: () => {
                                                                                    setShowExpenseItemData(data)
                                                                                    setShowUpdateExpenseModal(true)
                                                                                },
                                                                            }]
                                                                            : []),
                                                                        ...(constraintConfig.paymentMethod.corporateCreditCard !== data?.paymentMethod?.id &&
                                                                        data?.recordSourceId !== constraintConfig.recordSourceId.BankFeed
                                                                            ? [{
                                                                                key: 'delete',
                                                                                label: 'Delete Expense',
                                                                                tooltipId: 'Delete-Item',
                                                                                icon: <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3 text-hover-danger' svgClassName='svg-danger' />,
                                                                                onClick: () => {
                                                                                    setExpenseDeleteData(data?.expenseItemId);
                                                                                    deleteHandler();
                                                                                },
                                                                            }]
                                                                            : []),
                                                                        ...(data?.debitOrCreditIndicator !== constraintConfig.debitOrCreditIndicator.creditIndicator
                                                                            ? [{
                                                                                key: 'update',
                                                                                label: 'Update Item',
                                                                                tooltipId: 'Update-Item',
                                                                                icon: <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />,
                                                                                onClick: () => {
                                                                                    expenseItemById({ expenseItemId: data?.expenseItemId })
                                                                                    setViewOnlyData(false)
                                                                                    setShowExpenseItemData(data);
                                                                                    expenseItemModalHandler();
                                                                                },
                                                                            }]
                                                                            : []),
                                                                        {
                                                                            key: 'detail',
                                                                            label: 'Item Detail',
                                                                            tooltipId: 'Detail-Item',
                                                                            icon: <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-3' />,
                                                                            onClick: () => {
                                                                                expenseItemById({ expenseItemId: data?.expenseItemId })
                                                                                setViewOnlyData(true)
                                                                                setShowExpenseItemData(data);
                                                                                setShowExpenseItemDetailModal(true)
                                                                            },
                                                                        },
                                                                    ] as SsRowActionItem[]}
                                                                />
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                            </>
                                        ) :
                                            (
                                                <tr>
                                                    <td colSpan={15}>
                                                        <NoRecordFound />
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </>
                                }
                                {isLoading && (
                                    <tr>
                                        <td colSpan={15} className="text-center">
                                            <LoadingComponent />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table >
                    </div >
                    {!showMobileSkeleton && (
                    <Pagination
                        totalResult={data?.result?.totalRecords === 0 ? 0 : ((data?.result?.pageNumber - 1) * data?.result?.pageSize) + 1}
                        toResult={data?.result?.totalRecords === 0 ? 0 : Math.min(data?.result?.pageNumber * data?.result?.pageSize, data?.result?.totalRecords)}
                        ofResult={data?.result?.totalRecords}
                        onChange={handlePageSizeChange}
                        pageSize={pageSize}
                        onPageChange={handlePageClick}
                        pageCount={data?.result?.totalPages || 0}
                        currentPage={page}
                    />
                    )}
                    {/* end::Table container */}
                </div >


                {/* begin::Body */}
            </div >
        </>
    )
}

export default DraftItemsPage